import * as jwt_decode from 'jwt-decode';
import { AppState } from '../../../redux/reducers';

// store
import * as StatementStore from '../../../redux/middleware/PractitionerAccountStatement.Middleware';
import * as PersonsStore from '../../../redux/middleware/Persons.Middleware';
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware';
import { emptyStatementEntry } from '../../../redux/states/PractitionerAccountStatementState';
import * as InvoicesStore from '../../../redux/middleware/CashFlow-CreditorInvoices.Middleware';
import { creditorKey } from '../../../redux/states/CreditorInvoicesState';
import { buildPaymentReceiptTarget, getAzureTargetKey } from '../../../functions/AzureFileUpload.Functions';
import { CreditorInvoicesEntry } from '../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';

// models
import { PractitionerStatementEntry } from '../../../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';
import { nameOf } from '../../CashFlow/General/Cashflow.General.Functions';

interface IProps {
    /** GUID del profesional (Practitioner.userId = Debt.Creditor). */
    PractitionerId: string;
    PractitionerName: string;
}

interface ConnectedProps {
    /** El centro (tenant): deudor de las deudas y pagador de los pagos. */
    OwnerId: string;
    Statement: PractitionerStatementEntry;
    /** Nombre de una persona, empresa o profesional por su identificador. */
    NameOf: (identifier: string) => string;
    /** Facturas del profesional (CashFlow §12.2). */
    Invoices?: CreditorInvoicesEntry;
    /** Facturas que cubre el pago, si ya se leyeron o guardaron en esta sesión. */
    InvoiceCountOf?: (paymentId: number) => number | undefined;
    /** Comprobantes del pago, si el drawer de comprobantes ya los leyó. */
    ReceiptCountOf?: (paymentId: number) => number | undefined;
}

interface DispatchProps {
    LoadMoneySections: typeof StatementStore.actionCreators.LoadMoneySections;
    LoadPatientsSummary: typeof StatementStore.actionCreators.LoadPatientsSummary;
    LoadVisitsSummary: typeof StatementStore.actionCreators.LoadVisitsSummary;
    GetSettlementDebtsForPayment: typeof StatementStore.actionCreators.GetSettlementDebtsForPayment;
    GetAllPersons: typeof PersonsStore.actionCreators.GetAllPersons;
    GetAllPractitioners: typeof PractitionerStore.actionCreators.GetAllPractitioners;
    LoadCreditorInvoices: typeof InvoicesStore.actionCreators.LoadCreditorInvoices;
    UploadCreditorInvoice: typeof InvoicesStore.actionCreators.UploadCreditorInvoice;
    RetryRegisterCreditorInvoice: typeof InvoicesStore.actionCreators.RetryRegisterCreditorInvoice;
    RegisterSettlementInvoice: typeof InvoicesStore.actionCreators.RegisterSettlementInvoice;
    GetSettlementInvoiceCandidates: typeof InvoicesStore.actionCreators.GetSettlementInvoiceCandidates;
    UpdateCreditorInvoice: typeof InvoicesStore.actionCreators.UpdateCreditorInvoice;
    DeleteCreditorInvoice: typeof InvoicesStore.actionCreators.DeleteCreditorInvoice;
    GetPaymentInvoices: typeof InvoicesStore.actionCreators.GetPaymentInvoices;
    SetPaymentInvoices: typeof InvoicesStore.actionCreators.SetPaymentInvoices;
}

type InvoiceDispatchKeys =
    | 'LoadCreditorInvoices' | 'UploadCreditorInvoice' | 'RetryRegisterCreditorInvoice' | 'RegisterSettlementInvoice'
    | 'GetSettlementInvoiceCandidates' | 'UpdateCreditorInvoice' | 'DeleteCreditorInvoice' | 'GetPaymentInvoices' | 'SetPaymentInvoices';

/** Los thunks devuelven promesas: conectados, también. Los de facturas son opcionales (pantallas y pruebas previas). */
export type Props = IProps & ConnectedProps & {
    [K in Exclude<keyof DispatchProps, InvoiceDispatchKeys>]: (...args: Parameters<DispatchProps[K]>) => any;
} & {
    [K in InvoiceDispatchKeys]?: (...args: Parameters<DispatchProps[K]>) => any;
};

const tenantOf = (state: AppState): string => {
    try {
        return (jwt_decode.jwtDecode(state?.oidc?.user?.access_token ?? '') as any)?.tenantId ?? '';
    } catch {
        return '';
    }
};

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
    const tenantId = tenantOf(state);
    const persons = state.Persons?.list || [];
    const practitioners = state.Practitioners?.list || [];
    const invoices = state.CreditorInvoices;
    return {
        Invoices: invoices?.byCreditor?.[creditorKey(ownProps.PractitionerId)],
        InvoiceCountOf: (paymentId: number) => invoices?.invoiceCountByPayment?.[paymentId],
        ReceiptCountOf: (paymentId: number) => {
            const target = buildPaymentReceiptTarget(paymentId, ownProps.PractitionerId);
            const entry = target ? state.AzureFiles?.byTarget?.[getAzureTargetKey(target)] : undefined;
            return entry?.loadedAt ? entry.files.length : undefined;
        },
        OwnerId: tenantId,
        Statement: state.PractitionerAccountStatement?.byPractitioner?.[ownProps.PractitionerId] ?? emptyStatementEntry(),
        NameOf: (identifier: string) =>
            identifier && identifier.toLowerCase() === (ownProps.PractitionerId || '').toLowerCase() && ownProps.PractitionerName
                ? ownProps.PractitionerName
                : nameOf(persons, tenantId, identifier, practitioners)
    };
};

export const mapDispatchToProps = {
    LoadMoneySections: StatementStore.actionCreators.LoadMoneySections,
    LoadPatientsSummary: StatementStore.actionCreators.LoadPatientsSummary,
    LoadVisitsSummary: StatementStore.actionCreators.LoadVisitsSummary,
    GetSettlementDebtsForPayment: StatementStore.actionCreators.GetSettlementDebtsForPayment,
    GetAllPersons: PersonsStore.actionCreators.GetAllPersons,
    GetAllPractitioners: PractitionerStore.actionCreators.GetAllPractitioners,
    LoadCreditorInvoices: InvoicesStore.actionCreators.LoadCreditorInvoices,
    UploadCreditorInvoice: InvoicesStore.actionCreators.UploadCreditorInvoice,
    RetryRegisterCreditorInvoice: InvoicesStore.actionCreators.RetryRegisterCreditorInvoice,
    RegisterSettlementInvoice: InvoicesStore.actionCreators.RegisterSettlementInvoice,
    GetSettlementInvoiceCandidates: InvoicesStore.actionCreators.GetSettlementInvoiceCandidates,
    UpdateCreditorInvoice: InvoicesStore.actionCreators.UpdateCreditorInvoice,
    DeleteCreditorInvoice: InvoicesStore.actionCreators.DeleteCreditorInvoice,
    GetPaymentInvoices: InvoicesStore.actionCreators.GetPaymentInvoices,
    SetPaymentInvoices: InvoicesStore.actionCreators.SetPaymentInvoices
};
